import type { ReactNode } from 'react'
import * as RadixDialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '@/lib/cn'

/**
 * A modal.
 *
 * Radix handles the parts that are easy to get wrong and invisible when they
 * are: focus is trapped inside, Escape closes, the page behind cannot scroll,
 * and the trigger regains focus on close.
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  children: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
}) {
  const widths = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  }

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-ink/25 backdrop-blur-[1px] data-[state=open]:animate-in data-[state=open]:fade-in" />

        <RadixDialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2',
            'flex max-h-[calc(100vh-4rem)] flex-col rounded-xl bg-surface shadow-xl',
            widths[size],
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b border-border p-5">
            <div className="min-w-0">
              <RadixDialog.Title className="text-[17px] font-semibold tracking-tight">
                {title}
              </RadixDialog.Title>
              {description && (
                <RadixDialog.Description className="mt-1 text-sm text-ink-muted">
                  {description}
                </RadixDialog.Description>
              )}
            </div>

            <RadixDialog.Close
              className="-m-1 rounded-lg p-1 text-ink-muted transition-colors hover:bg-sunken hover:text-ink"
              aria-label="Close"
            >
              <X className="h-4 w-4" strokeWidth={2} />
            </RadixDialog.Close>
          </div>

          {/* The body scrolls, not the whole dialog — so the header and the
              action buttons stay reachable on a long form. */}
          <div className="scroll-slim min-h-0 flex-1 overflow-y-auto p-5">{children}</div>

          {footer && (
            <div className="flex items-center justify-end gap-2 border-t border-border p-5">
              {footer}
            </div>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  )
}
